"use strict";
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.Staking = void 0;
const bignumber_js_1 = __importDefault(require("bignumber.js"));
const internal_1 = require("../../internal");
const conversion_1 = require("../../utils/conversion");
const internal_2 = require("../../utils/internal");
/**
 * Handles Staking related functionality
 */
class Staking {
    /**
     * @hidden
     */
    constructor(context) {
        this.context = context;
        this.bond = (0, internal_2.createProcedureMethod)({
            getProcedureAndArgs: args => [internal_1.bondPolyx, Object.assign({}, args)],
        }, context);
        this.unbond = (0, internal_2.createProcedureMethod)({
            getProcedureAndArgs: args => [internal_1.updateBondedPolyx, Object.assign(Object.assign({}, args), { type: 'unbond' })],
        }, context);
        this.bondExtra = (0, internal_2.createProcedureMethod)({
            getProcedureAndArgs: args => [internal_1.updateBondedPolyx, Object.assign(Object.assign({}, args), { type: 'bondExtra' })],
        }, context);
        this.withdraw = (0, internal_2.createProcedureMethod)({
            getProcedureAndArgs: () => [internal_1.withdrawUnbondedPolyx, undefined],
            voidArgs: true,
        }, context);
        this.nominate = (0, internal_2.createProcedureMethod)({
            getProcedureAndArgs: args => [internal_1.nominateValidators, Object.assign({}, args)],
        }, context);
        this.setController = (0, internal_2.createProcedureMethod)({
            getProcedureAndArgs: args => [internal_1.setStakingController, args],
        }, context);
        this.setPayee = (0, internal_2.createProcedureMethod)({
            getProcedureAndArgs: args => [internal_1.setStakingPayee, args],
        }, context);
    }
    /**
     * Return information about nomination targets
     *
     * @note supports pagination
     */
    getValidators(paginationOpts) {
        return __awaiter(this, void 0, void 0, function* () {
            const { context, context: { polymeshApi: { query }, }, } = this;
            const { entries: rawValidators, lastKey: next } = yield (0, internal_2.requestPaginated)(query.staking.validators, {
                paginationOpts,
            });
            const data = rawValidators.map(([{ args: [rawAddress], }, rawValidatorPref,]) => {
                const address = (0, conversion_1.accountIdToString)(rawAddress);
                const account = new internal_1.Account({ address }, context);
                const { commission, blocked } = (0, conversion_1.rawValidatorPrefToCommission)(rawValidatorPref);
                return {
                    account,
                    commission,
                    blocked,
                };
            });
            return {
                next,
                data,
            };
        });
    }
    // eslint-disable-next-line require-jsdoc
    eraInfo(callback) {
        return __awaiter(this, void 0, void 0, function* () {
            const { context: { polymeshApi: { query }, }, context, } = this;
            const assembleResult = (rawActiveEra, rawCurrentEra, rawPlannedSession, rawTotalStaked) => {
                let activeEra;
                if (rawActiveEra.isNone) {
                    activeEra = { index: new bignumber_js_1.default(0), start: new bignumber_js_1.default(0) };
                }
                else {
                    activeEra = (0, conversion_1.activeEraStakingToActiveEraInfo)(rawActiveEra.unwrap());
                }
                let currentEra;
                if (rawCurrentEra.isNone) {
                    currentEra = new bignumber_js_1.default(0);
                }
                else {
                    currentEra = (0, conversion_1.u32ToBigNumber)(rawCurrentEra.unwrap());
                }
                const plannedSession = (0, conversion_1.u32ToBigNumber)(rawPlannedSession);
                const totalStaked = (0, conversion_1.u128ToBigNumber)(rawTotalStaked);
                return {
                    activeEra: activeEra.index,
                    activeEraStart: activeEra.start,
                    currentEra,
                    plannedSession,
                    totalStaked,
                };
            };
            if (callback) {
                context.assertSupportsSubscription();
                let rawActiveEra;
                let rawCurrentEra = context.createType('Option<u32>', undefined); // workaround "no use before defined" rule
                let rawPlannedSession;
                let rawTotalStaked;
                let initialized = false;
                const callCb = () => {
                    if (!initialized) {
                        return;
                    }
                    const result = assembleResult(rawActiveEra, rawCurrentEra, rawPlannedSession, rawTotalStaked);
                    // eslint-disable-next-line @typescript-eslint/no-floating-promises -- callback errors should be handled by the caller
                    callback(result);
                };
                const [activeUnsub, currentUnsub, plannedUnsub] = yield Promise.all([
                    query.staking.activeEra(activeEra => {
                        rawActiveEra = activeEra;
                        callCb();
                    }),
                    query.staking.currentEra((currentEra) => __awaiter(this, void 0, void 0, function* () {
                        rawCurrentEra = currentEra;
                        rawTotalStaked = yield query.staking.erasTotalStake(rawCurrentEra.unwrapOr(0));
                        callCb();
                    })),
                    query.staking.currentPlannedSession(plannedSession => {
                        rawPlannedSession = plannedSession;
                        callCb();
                    }),
                ]);
                rawTotalStaked = yield query.staking.erasTotalStake(rawCurrentEra.unwrapOr(0));
                const unsub = () => {
                    activeUnsub();
                    currentUnsub();
                    plannedUnsub();
                };
                initialized = true;
                callCb();
                return unsub;
            }
            const [rawActiveEra, rawCurrentEra, rawPlannedSession] = yield Promise.all([
                query.staking.activeEra(),
                query.staking.currentEra(),
                query.staking.currentPlannedSession(),
            ]);
            const rawTotalStaked = yield query.staking.erasTotalStake(rawCurrentEra.unwrapOr(0));
            return assembleResult(rawActiveEra, rawCurrentEra, rawPlannedSession, rawTotalStaked);
        });
    }
}
exports.Staking = Staking;
//# sourceMappingURL=Staking.js.map